import Config from 'config/settings';
import { globals } from 'config/globals';
import fastdom from 'fastdom';

import { DOMHelper } from 'core/DOMHelper';
import { TextScramble } from 'utils/TextScramble';
import DelayHelper from 'utils/timers';
import { device } from 'utils/device';
import { WorkViewCallbacks } from './work.types';

/**
 * DOM side of the work section: HTML injection, element refs, the intro
 * slide animation, the back-to-home button visibility and the (currently
 * unused) fake loader bar / swipe prompt.
 */
export class WorkView {
	private _dom: DOMHelper;
	private _callbacks: WorkViewCallbacks;

	private _html: string;
	private _isHTMLInjected: boolean = false; //Flag to stop the HTML inject action trigering twice
	private _htmlInjectionQeued: boolean = false; //Flagged when the view is required but not ready to setup

	public $intro: HTMLElement;
	public $loader: HTMLElement;
	public $loaderBar: HTMLElement;
	public $backButton: HTMLElement;
	public $swipePrompt: HTMLElement;
	public $swipePromptLabel: HTMLElement;

	private _swipePromptTextScramble: TextScramble;

	private _canShowBack: boolean = false;
	private _backToHomeIsVisible: boolean = false;

	private _introShowing: boolean = true;
	private _introX: number = 0;

	private _loaderActive: boolean = false;
	private _loaderPercentage = 0;
	private _totalFakeLoadUpdates;
	private _fakeLoaderFramecount;
	private _fakeLoaderTriggerAtFrame;
	private _loaderUpdateAmount: number;

	constructor(dom: DOMHelper, callbacks: WorkViewCallbacks) {
		this._dom = dom;
		this._callbacks = callbacks;
	}

	//HTML injection
	//-------------------------------------------------------------------------------------

	public get hasHTML(): boolean {
		return !!this._html;
	}

	/** Store the AJAX loaded HTML for the section */
	public storeHTML(html: string): void {
		this._html = html;
	}

	public get injectionQueued(): boolean {
		return this._htmlInjectionQeued;
	}

	public queueInjection(): void {
		this._htmlInjectionQeued = true;
	}

	/** When background loaded then inject the HTML */
	public injectHTML(onInjected: Function = null): void {
		//Only do this injecton once!

		if (this._isHTMLInjected || !this._html) {
			return;
		}

		this._dom.append(document.querySelector('body'), this._html, () => {
			const container = document.querySelector(`.work.-${this._callbacks.getDepartment()}`);
			this.$intro = container.querySelector('.work__slider__intro');
			this.$loader = container.querySelector('.workloader');
			this.$loaderBar = container.querySelector('.workloader__bar');
			this.$backButton = container.querySelector('.work__homebutton');
			this._callbacks.onContainerReady(container);
			if (onInjected) {
				onInjected();
			}
		});

		this._isHTMLInjected = true;
		this._htmlInjectionQeued = false;
	}

	/** Re-query element refs during setup */
	public queryRefs(container: Element): void {
		this.$intro = container.querySelector('.work__slider__intro');
		this.$backButton = container.querySelector('.work__homebutton');
	}

	//Swipe prompt
	//-------------------------------------------------------------------------------------

	public showDragToExploreGuide(deepline: boolean = false, fromProject: boolean = false) {
		if (fromProject === true) {
			return;
		}

		return;

		let dragLabel = globals.lastCordsFromTouch ? 'Swipe to continue' : 'Drag to continue';
		this._dom.setText(this.$swipePromptLabel, ' ');
		this._dom.removeClass(this.$swipePrompt, '-show');

		DelayHelper.setTimeout(() => {
			this._dom.addClass(this.$swipePrompt, '-show');
		}, 2000);

		DelayHelper.setTimeout(() => {
			this._swipePromptTextScramble.setText(dragLabel);
		}, 3000);
	}

	//Fake loader
	//-------------------------------------------------------------------------------------

	public get loaderActive(): boolean {
		return this._loaderActive;
	}

	public tickFakeLoader() {
		this._fakeLoaderFramecount++;

		if (this._fakeLoaderFramecount >= this._fakeLoaderTriggerAtFrame) {
			this._increaseLoader();
			this._totalFakeLoadUpdates++;
			this._fakeLoaderFramecount = 0;
		}
	}

	private _increaseLoader() {
		this._loaderPercentage += this._loaderUpdateAmount + Math.random() * 0.03;

		if (this._loaderPercentage >= 1) {
			this._loaderPercentage = 1;
			this._loaderComplete();
		}

		this.$loaderBar.style.transform = `scaleX(${this._loaderPercentage})`;
	}

	private _loaderComplete() {
		this._loaderActive = false;
		this._callbacks.onLoaderComplete();
	}

	//Intro slide
	//-------------------------------------------------------------------------------------

	public resetIntroX(): void {
		this._introX = 0;
	}

	/** Decide if the intro should be shown or hidden */
	public setIntroVisibleState(tileProgress: number, introActiveRange: number): void {
		if (!this._introShowing && tileProgress <= introActiveRange) {
			fastdom.mutate(() => {
				this.$intro.style.visibility = 'visible';
			});
			this._introShowing = true;
		} else if (this._introShowing && tileProgress > introActiveRange) {
			fastdom.mutate(() => {
				this.$intro.style.visibility = 'hidden';
			});

			this._introShowing = false;
		}
	}

	/** Update the animation of the intro */
	public updateIntroProgress(tileProgress: number, introActiveRange: number, innerWidth: number): void {
		if (!this._introShowing) {
			return;
		}

		if (this.$intro) {
			let windowMove = device.innerWidth < Config.mobileBreakpoint ? 1 : 0.65;

			let reduce = device.isPortraitDevice || innerWidth < 1500 ? 1 : 1;
			let introProgress = (tileProgress * reduce) / introActiveRange;
			this._introX = -1 * (innerWidth * windowMove * introProgress);

			fastdom.mutate(() => {
				this.$intro.style.transform = `translateX(${Math.floor(this._introX)}px)`;
				this.$intro.style.opacity = `${1 - 1 * (tileProgress / introActiveRange)}`;
			});

			this._callbacks.onIntroProgress(this._introX, introProgress);
		}
	}

	//Back to home button
	//-------------------------------------------------------------------------------------

	public set canShowBack(value: boolean) {
		this._canShowBack = value;
	}

	public set backToHomeVisible(value: boolean) {
		this._backToHomeIsVisible = value;
	}

	/** Check if the backToHome button should be shown */
	public checkBackToHomeVisible(homeViewReady: boolean, tileProgress: number, velocity: number, tileRatioShare: number): void {
		if (!this.$backButton) {
			return;
		}

		if (!this._backToHomeIsVisible && this._shouldBackShow(homeViewReady, tileProgress, velocity, tileRatioShare)) {
			this.showBackToHome();
		} else if (this._backToHomeIsVisible && !this._shouldBackShow(homeViewReady, tileProgress, velocity, tileRatioShare)) {
		}
	}

	public showBackToHome() {
		this._backToHomeIsVisible = true;
		this._dom.addClass(this.$backButton, '-show');
	}


	/** Logic for working out if back buttons meets conditions be shown */
	private _shouldBackShow(homeViewReady: boolean, tileProgress: number, velocity: number, tileRatioShare: number): boolean {
		if (
			this._canShowBack &&
			homeViewReady &&
			tileProgress <= tileRatioShare * 0.12 &&
			(tileProgress < 0.0001 || velocity <= 0)
		) {
			return true;
		}

		return false;
	}
}
